/** @format */

import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';

import { Observable } from 'rxjs';
import { map, retry } from 'rxjs/operators';

import { CustomHttpHeaders } from '@shared/data-access/enums';
import { Chain, Environment, ProductListSEO } from '@shared/data-access/interfaces';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { KillswitchService } from '../killswitch/killswitch.service';

@Injectable({
  providedIn: 'root',
})
export class CategoryDao {
  readonly BREADCRUMBS_BY_CATEGORY_ID_STATE_KEY_PREFIX = 'breadcrumb-';
  readonly CATEGORY_NAME_BY_ID_STATE_KEY_PREFIX = 'category-';

  private readonly killswitchService = inject(KillswitchService);
  private readonly isApiBrandDomainEnabled = this.killswitchService.killSwitchForCurrentChain(
    'enableSearchApiBrandDomain',
  );

  private readonly chain = inject(Chain);
  private readonly environment = inject(Environment);
  private readonly http = inject(HttpClient);
  protected readonly platformId = inject(PLATFORM_ID);
  private readonly state = inject(TransferState);

  /**
   * @description
   * Gets a category name by a given ID
   */
  public getCategoryNameByID$(identifier: string, retries = 0): Observable<ProductListSEO> {
    // Get unique key
    const STATE_KEY = makeStateKey<ProductListSEO>(
      `${this.CATEGORY_NAME_BY_ID_STATE_KEY_PREFIX}${identifier}`,
    );

    // Get unique key for category
    const stateData: any = this.state.get(STATE_KEY, null);

    // Check to see if state exists
    if (stateData) {
      // Return the existing state
      return new Observable((observer) => {
        observer.next(stateData);
        observer.complete();
      });
    }
    // Instantiate HTTP Headers (DEFAULTS)
    let httpHeaders = new HttpHeaders({});

    // Custom HTTP Headers
    httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');

    // Check to see if there is a timeout specified
    if (!isPlatformBrowser(this.platformId)) {
      // Set the timeout header
      httpHeaders = httpHeaders.set(
        CustomHttpHeaders.X_TIMEOUT,
        `${DCSG_CONSTANTS.timeouts.medium}`,
      );
    }

    // seoByChain is already always-on for this DAO; enableSearchApiBrandDomain only gates withCredentials
    const categoryUrl = `${this.environment.api.seoByChain}seo-category/v1/categories/identifier/${identifier}`;
    const httpParams = new HttpParams().set('storeId', this.chain.storeId);

    // HTTP Options
    const httpOptions = {
      headers: httpHeaders,
      params: httpParams,
      withCredentials: this.isApiBrandDomainEnabled(),
    };

    return this.http.get(categoryUrl, httpOptions).pipe(
      map((response: ProductListSEO) => {
        // Set the state
        this.state.set<ProductListSEO>(STATE_KEY, response);

        // Return the raw data
        return response;
      }),
      retry(retries),
    );
  }
}
